(() => {
  const initGallery = (gallery) => {
    const crops = Array.from(gallery.querySelectorAll('[data-fk-index]'));
    const lightbox = gallery.querySelector('[data-fk-lightbox]');
    const lightboxImage = gallery.querySelector('[data-fk-lightbox-image]');
    const lightboxCategory = gallery.querySelector('[data-fk-lightbox-category]');
    const lightboxCount = gallery.querySelector('[data-fk-lightbox-count]');
    const closeButton = gallery.querySelector('[data-fk-lightbox-close]');
    const previousButton = gallery.querySelector('[data-fk-lightbox-prev]');
    const nextButton = gallery.querySelector('[data-fk-lightbox-next]');
    let activeIndex = 0;
    let returnFocus = null;
    let touchStartX = null;
    let renderToken = 0;
    const preloadCache = new Map();

    if (!crops.length || !lightbox || !lightboxImage) return;

    const getCropImage = (crop) => crop?.querySelector('img');

    const primeCrop = (crop) => {
      const image = getCropImage(crop);
      if (!image) return Promise.resolve(null);

      const source = image.currentSrc || image.src;
      if (!source) return Promise.resolve(null);
      if (preloadCache.has(source)) return preloadCache.get(source);

      const preloader = new Image();
      preloader.decoding = 'async';
      preloader.src = source;

      const ready = preloader.complete && preloader.naturalWidth
        ? Promise.resolve({ source, image })
        : (typeof preloader.decode === 'function'
            ? preloader.decode().catch(() => undefined)
            : new Promise((resolve) => {
                preloader.addEventListener('load', resolve, { once: true });
                preloader.addEventListener('error', resolve, { once: true });
              }))
          .then(() => ({ source, image }));

      preloadCache.set(source, ready);
      return ready;
    };

    const showCrop = async (index) => {
      const nextIndex = (index + crops.length) % crops.length;
      const crop = crops[nextIndex];
      const image = crop.querySelector('img');
      if (!image) return;

      const token = ++renderToken;
      const prepared = await primeCrop(crop);
      if (!prepared || token !== renderToken) return;

      activeIndex = nextIndex;
      lightboxImage.src = prepared.source;
      lightboxImage.alt = image.alt;
      lightboxCategory.textContent = crop.dataset.fkCategoryLabel || '';
      lightboxCount.textContent = `${activeIndex + 1} / ${crops.length} · #${crop.dataset.fkCropNumber}`;
    };

    const openLightbox = async (index, trigger) => {
      returnFocus = trigger;
      await showCrop(index);
      lightbox.hidden = false;
      document.body.classList.add('fk-gallery-is-open');
      closeButton.focus();
    };

    const closeLightbox = () => {
      lightbox.hidden = true;
      lightboxImage.removeAttribute('src');
      document.body.classList.remove('fk-gallery-is-open');
      if (returnFocus) returnFocus.focus();
    };

    crops.forEach((crop, index) => {
      crop.addEventListener('click', () => openLightbox(index, crop));
      crop.addEventListener('pointerenter', () => primeCrop(crop), { once: true });
      crop.addEventListener('focus', () => primeCrop(crop), { once: true });
      crop.addEventListener('touchstart', () => primeCrop(crop), { once: true, passive: true });
    });

    if ('IntersectionObserver' in window) {
      const preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          primeCrop(entry.target);
          preloadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '900px 0px' });
      crops.forEach((crop) => preloadObserver.observe(crop));
    } else {
      crops.slice(0, 12).forEach(primeCrop);
    }

    closeButton.addEventListener('click', closeLightbox);
    previousButton.addEventListener('click', () => showCrop(activeIndex - 1));
    nextButton.addEventListener('click', () => showCrop(activeIndex + 1));

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });

    lightbox.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });

    lightbox.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      if (Math.abs(delta) > 55) showCrop(activeIndex + (delta < 0 ? 1 : -1));
      touchStartX = null;
    }, { passive: true });

    document.addEventListener('keydown', (event) => {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') showCrop(activeIndex - 1);
      if (event.key === 'ArrowRight') showCrop(activeIndex + 1);
    });

    const categoryLinks = Array.from(gallery.querySelectorAll('[data-fk-category-link]'));
    const categories = Array.from(gallery.querySelectorAll('[data-fk-category]'));
    if ('IntersectionObserver' in window && categoryLinks.length === categories.length) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        categoryLinks.forEach((link) => {
          link.classList.toggle('is-active', link.hash === `#${visible.target.id}`);
        });
      }, { rootMargin: '-20% 0px -68% 0px', threshold: [0, 0.05, 0.2] });
      categories.forEach((category) => observer.observe(category));
    }
  };

  const init = () => document.querySelectorAll('[data-fk-gallery]').forEach(initGallery);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
